import { Inbox } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { showErrorToast, showSuccessToast } from "../../../../../lib/toast";
import { fetchCaseDocuments } from "../../../../../thunks/caseDocument";
import { CommentEntityType, CommentThreadType } from "../../../../../api/comments";
import { useAppDispatch, useAppSelector } from "../../../../../app/hooks";
import { SaveButtonV2 } from "../../../../../components/ui/button/v2/SaveButton";
import { MDXEditorMethods } from "../../../../../components/ui/editor";
import { ReportSkeleton } from "../../../../../components/ui/loading/ReportSkeleton";
import {
  SecondaryTabsList,
  SecondaryTabsTrigger,
  Tabs,
  TabsContent,
} from "../../../../../components/ui/tabs/Tabs";
import CommentMainPage from "../../../../comments/components/CommentMainPage";
import ReportsSidebar, {
  PartiesInvolvedSkeletonLoader,
} from "../../../../partiesinvolved/components/PartiesInvolvedSidebar";
import {
  selectPartiesInvolvedCaseId,
  selectPartiesInvolvedStatus,
} from "../../../../partiesinvolved/logic/PartiesInvolvedSelectors";
import { selectSCNResponseEntities, selectSCNResponseStatus } from "../logic/scnResponseReportsSlice";
import { fetchSCNResponseReportsLegacyThunk, fetchSCNResponseReportsThunk, putSCNResponseLegacyThunk, putSCNResponseThunk } from "../logic/scnResponseReportsThunk";
import ReportHeader from "../../CommonReportHeader";
import { selectSCNReportEntities, selectSCNStatus } from "../logic/scnReportsSlice";
import { fetchSCNReportsLegacyThunk, fetchSCNReportsThunk, upsertSCNReportLegacyThunk, upsertSCNReportThunk } from "../logic/scnReportsThunk";
import styles from "./SCNPage.module.css";
import SCNResponseTabContainer from "./SCNResponseTabContainer";
import SCNTabContainer from "./SCNTabContainer";
import { createSCNTabItems } from "./utils";
import { isCreditAccountStakeholdersRevampEnabled, isReportsPolicyNormalizationEnabled } from "../../../../configs/logic/Slice";
import { fetchActionsAgainstPerpetratorsThunk } from "../../../../../features/recommendations/logic/ActionsAgainstPerpetratorsThunk";
import { selectPartiesWithActions, selectPerpetratorActionsStatus } from "../../../../../features/recommendations/logic/ActionsAgainstPerpetratorsSlice";

export const ShowCauseNoticeResponsePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: case_id } = useParams<{ id: string }>();

  const [activePartyID, setActivePartyID] = useState<string | null>(null);
  const [localNoticeContent, setLocalNoticeContent] = useState<string>("");
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [isSavingNoResponse, setIsSavingNoResponse] = useState(false);

  const [noResponsePartyId, setNoResponsePartyId] = useState<string | null>(
    null,
  );

  const editorRef = useRef<MDXEditorMethods>(null);

  const isV2Enabled = useAppSelector(isReportsPolicyNormalizationEnabled);
  const isStakeholdersRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);
  const featureFlags = useAppSelector((state) => state.configs.featureFlags);
  const areFlagsLoaded = Object.keys(featureFlags).length > 0;
  const notices = useAppSelector(selectSCNReportEntities);
  const responses = useAppSelector(selectSCNResponseEntities);
  const scnStatus = useAppSelector(selectSCNStatus);
  const scnResponseStatus = useAppSelector(selectSCNResponseStatus);
  const isSaving = scnStatus === "saving" || isSavingNoResponse;

  const partiesStatus = useAppSelector(selectPartiesInvolvedStatus);
  const storedPartiesCaseId = useAppSelector(selectPartiesInvolvedCaseId);
  const storedReportsCaseId = useAppSelector(
    (state: any) => state.reportsData.caseId,
  ) as string | null;
  const partiesWithRecommendedActions = useAppSelector(
    selectPartiesWithActions,
  );

  const perpetratorActionsStatus = useAppSelector(selectPerpetratorActionsStatus);
  
  const isPartiesLoading = partiesStatus === "loading" || perpetratorActionsStatus === "loading";
  const isSCNLoading = scnStatus === "loading";
  const isSCNResponseLoading = scnResponseStatus === "loading";

  const isStaleCaseContext =
    !!case_id &&
    ((!!storedPartiesCaseId && storedPartiesCaseId !== case_id) ||
      (!!storedReportsCaseId && storedReportsCaseId !== case_id));

  const isDataLoading =
    isSCNLoading ||
    isSCNResponseLoading ||
    isPartiesLoading ||
    isStaleCaseContext;

  useEffect(() => {
    if (
      !isPartiesLoading &&
      partiesWithRecommendedActions.length > 0 &&
      !activePartyID
    ) {
      setActivePartyID(partiesWithRecommendedActions[0].party_id);
    }
  }, [isPartiesLoading, partiesWithRecommendedActions, activePartyID]);

  useEffect(() => {
    if (case_id && areFlagsLoaded) {
      dispatch(isV2Enabled ? fetchSCNReportsThunk({ case_id }) : fetchSCNReportsLegacyThunk({ case_id }));
      dispatch(isV2Enabled ? fetchSCNResponseReportsThunk({ case_id }) : fetchSCNResponseReportsLegacyThunk({ case_id }));
      dispatch(fetchCaseDocuments({ case_id }));
      if(isStakeholdersRevampEnabled){
        dispatch(fetchActionsAgainstPerpetratorsThunk({caseId: case_id}))
      }
    }
  }, [case_id, dispatch, areFlagsLoaded, isV2Enabled, isStakeholdersRevampEnabled]);

  const selectedNotice = activePartyID ? notices[activePartyID] : undefined;

  useEffect(() => {
    if (selectedNotice?.content) {
      setLocalNoticeContent(selectedNotice.content);
      editorRef.current?.setMarkdown(selectedNotice.content);
    } else {
      setLocalNoticeContent("");
      editorRef.current?.setMarkdown("");
    }
  }, [selectedNotice]);

  useEffect(() => {
    const currentContent = localNoticeContent || "";
    const originalContent = selectedNotice?.content || "";
    const hasChanges = originalContent !== currentContent;
    setHasUnsavedChanges(hasChanges);
  }, [localNoticeContent, selectedNotice?.content]);

  const handlePartySelect = (party_id: string) => {
    setActivePartyID(party_id);
  };

  const handleNoticeChange = (partyId: string, content: string) => {
    if (partyId === activePartyID) {
      setLocalNoticeContent(content);
      editorRef.current?.setMarkdown(content);
    }
  };

  const handleSave = async () => {
    if (!case_id || !hasUnsavedChanges) return;

    if (activePartyID && selectedNotice && localNoticeContent !== selectedNotice.content) {
     dispatch(isV2Enabled
        ? upsertSCNReportThunk({ case_id, report_id: selectedNotice!.report_id, request: {party_id: activePartyID!, content: localNoticeContent } })
        : upsertSCNReportLegacyThunk({ case_id, report_id: selectedNotice!.report_id, request: { party_id: activePartyID!, content: localNoticeContent } })
     );
    }

    if (noResponsePartyId) {
      setIsSavingNoResponse(true);
      try {
        if (isV2Enabled) {
            await dispatch(
                putSCNResponseThunk({
                    case_id,
                    report_id: noResponsePartyId,
                    request: {
                        party_id: noResponsePartyId,
                        is_response_received: false,
                    },
                })
            );
        } else {
            await dispatch(
                putSCNResponseLegacyThunk({
                    case_id,
                    report_id: noResponsePartyId,
                    party_id: noResponsePartyId,
                    is_response_received: false,
                })
            );
        }

        showSuccessToast("No Response Updated");
      } catch (error) {
        console.error("Failed to save no response:", error);
        showErrorToast("Failed To update No Response");
      } finally {
        setIsSavingNoResponse(false);
        setNoResponsePartyId(null);
        setHasUnsavedChanges(false);
      }
    }
  };

  const handleConfirmNoResponse = (partyId: string) => {
    setNoResponsePartyId(partyId);
    setHasUnsavedChanges(true);
  };

  const tabItems = createSCNTabItems(
    isStaleCaseContext ? [] : partiesWithRecommendedActions,
    isStaleCaseContext ? {} : notices,
    isStaleCaseContext ? {} : responses,
    isDataLoading,
  );

  const showEmptyState = !isDataLoading && tabItems.length === 0;

  const selectedParty = partiesWithRecommendedActions.find(
    (party) => party.party_id === activePartyID,
  );
  const selectedPartyName = selectedParty?.name;

  return (
    <>
      {/* Top header with Save & Navigation Breadcrumb - Outside the main container */}
      <div className={`${styles.topHeader} flex items-center justify-end`}>

        {!showEmptyState && (
          <SaveButtonV2
            disabled={!hasUnsavedChanges || isSaving}
            onClick={handleSave}
            buttonText={isSaving ? "Saving..." : "Save"}
          />
        )}
      </div>

      <div className={styles.pageContainer}>
        {/* Parties Involved Sidebar */}
        {!showEmptyState && (
          <div className={styles.sidebarWrapper}>
            {isPartiesLoading || isStaleCaseContext ? (
              <PartiesInvolvedSkeletonLoader />
            ) : (
              <ReportsSidebar
                items={tabItems}
                activeSelection={activePartyID}
                onSelectionChange={handlePartySelect}
              />
            )}
          </div>
        )}

        {/* Main Content Area */}
        <div className={styles.mainContent}>
          {isDataLoading ? (
            <ReportSkeleton />
          ) : showEmptyState ? (
            <div className={styles.contentArea}>
              <div className={styles.generateContainer}>
                <div className={styles.generateCard}>
                  <div className={styles.generateIcon}>
                    <Inbox size={24} />
                  </div>
                  <div className={styles.generateTextContent}>
                    <h3 className={styles.generateTitle}>
                      Show Cause Notice not yet available
                    </h3>
                    <p className={styles.generateDescription}>
                      Add parties with recommended actions to generate the
                      notice.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            selectedParty && (
              <div className={styles.contentArea}>
                <ReportHeader
                  partyName={selectedParty.name}
                  partyType={selectedParty.party_type}
                />
                <div>
                  {/* SCN Pair Container */}
                  <Tabs defaultValue="scn-notice" className="w-full h-full">
                    <SecondaryTabsList className="bg-[#F9FAFB]">
                      <SecondaryTabsTrigger value="scn-notice">
                        Notice
                      </SecondaryTabsTrigger>
                      <SecondaryTabsTrigger value="scn-response">
                        Response
                      </SecondaryTabsTrigger>
                    </SecondaryTabsList>

                    <TabsContent value="scn-notice">
                      <SCNTabContainer
                        ref={editorRef}
                        partyId={activePartyID ?? ""}
                        partyName={selectedPartyName ?? ""}
                        noticeData={selectedNotice}
                        currentContent={localNoticeContent}
                        onNoticeChange={handleNoticeChange}
                        isSaving={isSaving}
                      />
                    </TabsContent>

                    <TabsContent value="scn-response">
                      <SCNResponseTabContainer
                        partyId={activePartyID ?? ""}
                        isSaving={isSaving}
                        isLoading={isSCNResponseLoading}
                        onConfirmNoResponse={handleConfirmNoResponse}
                      />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            )
          )}
        </div>
      </div>
      {!isDataLoading && !showEmptyState && case_id && (
        <div className="w-full bg-[#F3F4F6]">
          <div className="h-4" />
          <CommentMainPage
            caseId={case_id}
            threadType={CommentThreadType.SCN_PROCEEDING}
            entityId={case_id}
            entityType={CommentEntityType.CASE}
            className="mx-6"
          />
          <div className="h-14" />
        </div>
      )}
    </>
  );
};
